import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Avatar2D from "@/components/Avatar2D";
import LeaderCrown from "@/components/LeaderCrown";
import { profilePath } from "@/components/PlayerLink";
import { characterFor } from "@/lib/avatarConfig";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Trophy } from "@phosphor-icons/react";

const MEDAL = ["text-amber-500", "text-slate-400", "text-orange-700 dark:text-orange-400"];

// Header icon beside "How to play": opens the top 5 players by Elo (bots
// aren't accounts, so never listed), per game type (Jack, 2026-10-28):
// Jangle, Raider Races or Live Games (each its own Elo; Live Games added
// 2026-11-02), picked with a toggle at the top.
// Tapping a player opens their profile.
const SECTIONS = [
  { key: "players", title: "Jangle", empty: "No ranked players yet." },
  { key: "race", title: "Raider Races", empty: "No races against people yet." },
  { key: "live", title: "Live Games", empty: "No Live Games against people yet." },
];
const CROWN_CATEGORY = { players: "jangle", race: "race", live: "live" };
const ROW_PREFIX = { players: "", race: "race-", live: "live-" };
export default function Leaderboard({ className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState(null);
  const [kind, setKind] = useState("players");

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) return;
    // Fresh every time it opens, so it never shows stale Elo.
    api.get("/players/top")
      .then(({ data }) => setBoards({ players: data.players || [], race: data.race || [], live: data.live || [] }))
      .catch(() => setBoards((b) => b || { players: [], race: [], live: [] }));
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ${className}`}
          aria-label="Leaderboard"
          title="Leaderboard"
          data-testid="leaderboard-trigger"
        >
          <Trophy size={18} weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72 p-4 bg-surface" data-testid="leaderboard-card">
        <h3 className="font-display text-lg font-bold tracking-tight mb-2">Leaderboard</h3>
        <div className="grid grid-cols-3 gap-1 p-1 mb-3 rounded-full bg-muted" role="tablist" data-testid="leaderboard-toggle">
          {SECTIONS.map((sec) => (
            <button
              key={sec.key}
              type="button"
              role="tab"
              aria-selected={kind === sec.key}
              onClick={() => setKind(sec.key)}
              className={`h-7 px-1 rounded-full text-[11px] leading-tight font-bold whitespace-nowrap transition-colors ${kind === sec.key
                ? "bg-surface text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`leaderboard-tab-${sec.key}`}
            >
              {sec.title}
            </button>
          ))}
        </div>
        {boards === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : SECTIONS.filter((sec) => sec.key === kind).map((sec) => (
          <section key={sec.key} data-testid={`leaderboard-${sec.key}`}>
            {(boards[sec.key] || []).length === 0 ? (
              <p className="text-xs text-muted-foreground py-2 text-center">{sec.empty}</p>
            ) : (
              <ol className="space-y-1.5">
                {boards[sec.key].map((p) => {
                  const me = p.id === user?.id;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          navigate(profilePath(p.id, user));
                        }}
                        className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-xl border text-left transition-colors ${
                          me ? "border-brand/40 bg-brand/10" : "border-transparent hover:bg-muted"}`}
                        data-testid={`leaderboard-${ROW_PREFIX[sec.key]}row-${p.rank}`}
                      >
                        <span className={`w-4 text-center font-display font-black ${MEDAL[p.rank - 1] || "text-muted-foreground"}`}>
                          {p.rank}
                        </span>
                        <span className="relative w-9 flex-none">
                          <Avatar2D character={characterFor(p.id, p.avatar3d)} className="w-9 aspect-[240/256]" />
                          {p.rank === 1 && <LeaderCrown userId={p.id} category={CROWN_CATEGORY[sec.key]} className="raider-crown w-7" />}
                        </span>
                        <span className="flex-1 min-w-0 font-medium text-sm truncate">{p.username}</span>
                        <span className="font-display font-bold text-sm tabular-nums">{p.elo_rating}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        ))}
      </PopoverContent>
    </Popover>
  );
}
